import { useState, useMemo } from 'react';
import {
  Download,
  RefreshCw,
  FileSpreadsheet,
  TrendingUp,
  Receipt,
  Calendar,
  IndianRupee,
  Code,
  Table as TableIcon,
  ShieldCheck,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatPaise } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useGstReport, useRevenueReport, useTdsReport } from '@/hooks/use-admin';

type Tab = 'revenue' | 'gst' | 'tds';

interface GstReportItem {
  id: string;
  invoiceNumber: string;
  fiscalYear: string;
  paidAt: string;
  placeOfSupplyState: string | null;
  hsnSacCode: string | null;
  gstPercentage: string;
  subtotalPaise: string;
  cgstPaise: string;
  sgstPaise: string;
  igstPaise: string;
  totalPaise: string;
  clientGstin: string | null;
  transactionCode: string;
}

interface GstReportResponse {
  window: { fromDate: string; toDate: string };
  count: number;
  totals: {
    subtotalPaise: string;
    cgstPaise: string;
    sgstPaise: string;
    igstPaise: string;
    totalPaise: string;
  };
  items: GstReportItem[];
}

interface TdsReportItem {
  id: string;
  paidAt: string;
  transactionCode: string;
  associate: { id: string; email: string; firstName: string; lastName: string };
  pan: string | null;
  sectionCode: string | null;
  tdsPercentage: string;
  grossPaise: string;
  tdsPaise: string;
  netPaise: string;
  reference: string | null;
}

interface TdsReportResponse {
  window: { fromDate: string; toDate: string };
  count: number;
  totals: {
    grossPaise: string;
    tdsPaise: string;
    netPaise: string;
  };
  items: TdsReportItem[];
}

interface RevenueBucketItem {
  bucket: string;
  invoiceCount: number;
  subtotalPaise: string;
  platformFeePaise: string;
  associatePayoutPaise: string;
}

interface RevenueReportResponse {
  window: { fromDate: string; toDate: string; bucket: 'day' | 'month' };
  items: RevenueBucketItem[];
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function downloadCsv(filename: string, headers: string[], rows: (string | number)[][]) {
  const escape = (val: string | number | null | undefined) => {
    const str = val == null ? '' : String(val);
    return `"${str.replace(/"/g, '""')}"`;
  };
  const csvContent = [
    headers.map(escape).join(','),
    ...rows.map((row) => row.map(escape).join(',')),
  ].join('\r\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function toExclusiveEndDate(dateStr: string): string {
  if (!dateStr) return dateStr;
  const d = new Date(`${dateStr}T00:00:00Z`);
  if (isNaN(d.getTime())) return dateStr;
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

export function AdminReportsPage() {
  const [tab, setTab] = useState<Tab>('revenue');
  const [viewMode, setViewMode] = useState<'table' | 'json'>('table');
  const [revenueBucket, setRevenueBucket] = useState<'month' | 'day'>('month');

  const now = new Date();
  const monthStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const [from, setFrom] = useState(isoDate(monthStart));
  const [to, setTo] = useState(isoDate(now));

  const toExclusive = useMemo(() => toExclusiveEndDate(to), [to]);

  const revenueQuery = useRevenueReport(from, toExclusive, revenueBucket);
  const gstQuery = useGstReport(from, toExclusive);
  const tdsQuery = useTdsReport(from, toExclusive);

  const revenueData = revenueQuery.data as RevenueReportResponse | undefined;
  const gstData = gstQuery.data as GstReportResponse | undefined;
  const tdsData = tdsQuery.data as TdsReportResponse | undefined;

  const setPreset = (preset: 'thisMonth' | 'lastMonth' | 'thisFY') => {
    const currentDate = new Date();
    if (preset === 'thisMonth') {
      const start = new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), 1));
      setFrom(isoDate(start));
      setTo(isoDate(currentDate));
    } else if (preset === 'lastMonth') {
      const start = new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth() - 1, 1));
      const end = new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), 0));
      setFrom(isoDate(start));
      setTo(isoDate(end));
    } else if (preset === 'thisFY') {
      const year = currentDate.getUTCMonth() >= 3 ? currentDate.getUTCFullYear() : currentDate.getUTCFullYear() - 1;
      const start = new Date(Date.UTC(year, 3, 1));
      setFrom(isoDate(start));
      setTo(isoDate(currentDate));
    }
  };

  const handleExportCsv = () => {
    if (tab === 'revenue' && revenueData) {
      const headers = [
        'Period Bucket',
        'Invoices Count',
        'Gross Subtotal (INR)',
        'Platform Fee (INR)',
        'Associate Split (INR)',
      ];
      const rows = revenueData.items.map((item) => [
        item.bucket,
        item.invoiceCount,
        (Number(item.subtotalPaise) / 100).toFixed(2),
        (Number(item.platformFeePaise) / 100).toFixed(2),
        (Number(item.associatePayoutPaise) / 100).toFixed(2),
      ]);
      downloadCsv(`ecregi-revenue-report-${from}-to-${to}.csv`, headers, rows);
    } else if (tab === 'gst' && gstData) {
      const headers = [
        'Invoice Number',
        'Transaction Code',
        'Paid Date',
        'Place of Supply State',
        'Client GSTIN',
        'HSN/SAC Code',
        'GST Rate %',
        'Taxable Subtotal (INR)',
        'CGST (INR)',
        'SGST (INR)',
        'IGST (INR)',
        'Total Amount (INR)',
      ];
      const rows = gstData.items.map((item) => [
        item.invoiceNumber,
        item.transactionCode,
        item.paidAt ? new Date(item.paidAt).toLocaleDateString('en-IN') : '',
        item.placeOfSupplyState ?? 'N/A',
        item.clientGstin ?? 'Unregistered',
        item.hsnSacCode ?? '',
        item.gstPercentage,
        (Number(item.subtotalPaise) / 100).toFixed(2),
        (Number(item.cgstPaise) / 100).toFixed(2),
        (Number(item.sgstPaise) / 100).toFixed(2),
        (Number(item.igstPaise) / 100).toFixed(2),
        (Number(item.totalPaise) / 100).toFixed(2),
      ]);
      downloadCsv(`ecregi-gstr1-report-${from}-to-${to}.csv`, headers, rows);
    } else if (tab === 'tds' && tdsData) {
      const headers = [
        'Paid Date',
        'Transaction Code',
        'Associate Name',
        'Associate Email',
        'Associate PAN',
        'Section Code',
        'TDS %',
        'Gross Payout (INR)',
        'TDS Deducted (INR)',
        'Net Paid (INR)',
        'Bank Reference',
      ];
      const rows = tdsData.items.map((item) => [
        item.paidAt ? new Date(item.paidAt).toLocaleDateString('en-IN') : '',
        item.transactionCode,
        `${item.associate.firstName} ${item.associate.lastName}`.trim(),
        item.associate.email,
        item.pan ?? 'Not Provided',
        item.sectionCode ?? '',
        item.tdsPercentage,
        (Number(item.grossPaise) / 100).toFixed(2),
        (Number(item.tdsPaise) / 100).toFixed(2),
        (Number(item.netPaise) / 100).toFixed(2),
        item.reference ?? '',
      ]);
      downloadCsv(`ecregi-form26q-tds-report-${from}-to-${to}.csv`, headers, rows);
    }
  };

  const handleRefresh = () => {
    if (tab === 'revenue') revenueQuery.refetch();
    if (tab === 'gst') gstQuery.refetch();
    if (tab === 'tds') tdsQuery.refetch();
  };

  const isCurrentLoading =
    tab === 'revenue' ? revenueQuery.isLoading : tab === 'gst' ? gstQuery.isLoading : tdsQuery.isLoading;

  return (
    <div className="w-full px-4 py-6 md:px-6 md:py-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Reports &amp; Statutory Registers</h1>
          <p className="text-sm text-gray-600">
            Export financial summaries, GST output register (GSTR-1), and TDS registers (Form 26Q).
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Toggle View Mode */}
          <div className="flex rounded-lg border border-gray-200 bg-white p-0.5 shadow-2xs">
            <button
              type="button"
              onClick={() => setViewMode('table')}
              className={cn(
                'flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                viewMode === 'table' ? 'bg-brand-700 text-white shadow-2xs' : 'text-gray-600 hover:text-gray-900',
              )}
            >
              <TableIcon className="h-3.5 w-3.5" />
              Table
            </button>
            <button
              type="button"
              onClick={() => setViewMode('json')}
              className={cn(
                'flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                viewMode === 'json' ? 'bg-brand-700 text-white shadow-2xs' : 'text-gray-600 hover:text-gray-900',
              )}
            >
              <Code className="h-3.5 w-3.5" />
              JSON
            </button>
          </div>

          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={isCurrentLoading}
            className="h-8 gap-1.5 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 shadow-2xs"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', isCurrentLoading && 'animate-spin')} />
            Refresh
          </Button>

          <Button
            variant="primary"
            onClick={handleExportCsv}
            disabled={isCurrentLoading}
            className="h-8 gap-1.5 px-3 py-1 text-xs font-medium bg-brand-700 hover:bg-brand-800 text-white shadow-xs"
          >
            <Download className="h-3.5 w-3.5" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Date Filter & Presets Toolbar */}
      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white p-3.5 shadow-2xs">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-400" />
            <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700">
              From:
              <Input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="h-8 w-auto text-xs py-1 px-2.5"
              />
            </label>
            <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700">
              To:
              <Input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="h-8 w-auto text-xs py-1 px-2.5"
              />
            </label>
          </div>

          {/* Quick Presets */}
          <div className="flex items-center gap-1 text-xs">
            <span className="text-gray-400">Presets:</span>
            <button
              type="button"
              onClick={() => setPreset('thisMonth')}
              className="rounded px-2 py-1 text-gray-600 hover:bg-gray-100 hover:text-gray-900 font-medium"
            >
              This Month
            </button>
            <button
              type="button"
              onClick={() => setPreset('lastMonth')}
              className="rounded px-2 py-1 text-gray-600 hover:bg-gray-100 hover:text-gray-900 font-medium"
            >
              Last Month
            </button>
            <button
              type="button"
              onClick={() => setPreset('thisFY')}
              className="rounded px-2 py-1 text-gray-600 hover:bg-gray-100 hover:text-gray-900 font-medium"
            >
              This FY
            </button>
          </div>
        </div>

        {tab === 'revenue' && (
          <div className="flex items-center gap-1.5 text-xs text-gray-600">
            <span>Interval:</span>
            <select
              value={revenueBucket}
              onChange={(e) => setRevenueBucket(e.target.value as 'month' | 'day')}
              className="h-8 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 focus:outline-hidden focus:ring-1 focus:ring-brand-500"
            >
              <option value="month">Monthly</option>
              <option value="day">Daily</option>
            </select>
          </div>
        )}
      </div>

      {/* Tabs Switcher */}
      <div className="mt-6 flex items-center gap-2 overflow-x-auto no-scrollbar scroll-smooth py-1 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap border-b border-gray-200 pb-2">
        <button
          type="button"
          onClick={() => setTab('revenue')}
          className={cn(
            'shrink-0 flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-semibold transition-all whitespace-nowrap',
            tab === 'revenue'
              ? 'bg-brand-700 text-white shadow-2xs'
              : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200/80',
          )}
        >
          <TrendingUp className="h-3.5 w-3.5" />
          Revenue Summary
        </button>
        <button
          type="button"
          onClick={() => setTab('gst')}
          className={cn(
            'shrink-0 flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-semibold transition-all whitespace-nowrap',
            tab === 'gst'
              ? 'bg-brand-700 text-white shadow-2xs'
              : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200/80',
          )}
        >
          <Receipt className="h-3.5 w-3.5" />
          GST Register (GSTR-1)
        </button>
        <button
          type="button"
          onClick={() => setTab('tds')}
          className={cn(
            'shrink-0 flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-semibold transition-all whitespace-nowrap',
            tab === 'tds'
              ? 'bg-brand-700 text-white shadow-2xs'
              : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200/80',
          )}
        >
          <ShieldCheck className="h-3.5 w-3.5" />
          TDS Register (Form 26Q)
        </button>
      </div>

      {/* KPI Summary Cards */}
      <section className="mt-5">
        {tab === 'revenue' && <RevenueKpis data={revenueData} loading={revenueQuery.isLoading} />}
        {tab === 'gst' && <GstKpis data={gstData} loading={gstQuery.isLoading} />}
        {tab === 'tds' && <TdsKpis data={tdsData} loading={tdsQuery.isLoading} />}
      </section>

      {/* Content Section (Table or Raw JSON) */}
      <section className="mt-6 rounded-xl border border-gray-200 bg-white shadow-2xs overflow-hidden">
        {viewMode === 'json' ? (
          <div className="p-4">
            <h3 className="text-xs font-mono font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Raw Wire Response ({tab})
            </h3>
            <pre className="max-h-[550px] overflow-auto rounded-lg bg-gray-900 p-4 text-[11px] font-mono text-gray-100">
              {JSON.stringify(
                tab === 'revenue' ? revenueData : tab === 'gst' ? gstData : tdsData,
                null,
                2,
              )}
            </pre>
          </div>
        ) : (
          <div>
            {tab === 'revenue' && (
              <RevenueTable
                data={revenueData}
                loading={revenueQuery.isLoading}
                error={revenueQuery.isError}
              />
            )}
            {tab === 'gst' && (
              <GstTable data={gstData} loading={gstQuery.isLoading} error={gstQuery.isError} />
            )}
            {tab === 'tds' && (
              <TdsTable data={tdsData} loading={tdsQuery.isLoading} error={tdsQuery.isError} />
            )}
          </div>
        )}
      </section>
    </div>
  );
}

// ── KPI Summary Cards ──────────────────────────────────────────────

function RevenueKpis({ data, loading }: { data?: RevenueReportResponse; loading: boolean }) {
  const totals = useMemo(() => {
    if (!data?.items) return { count: 0, subtotal: 0n, fee: 0n, payout: 0n };
    return data.items.reduce(
      (acc, item) => {
        acc.count += item.invoiceCount;
        acc.subtotal += BigInt(item.subtotalPaise);
        acc.fee += BigInt(item.platformFeePaise);
        acc.payout += BigInt(item.associatePayoutPaise);
        return acc;
      },
      { count: 0, subtotal: 0n, fee: 0n, payout: 0n },
    );
  }, [data]);

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <KpiCard label="Paid Invoices" value={loading ? '…' : totals.count} icon={Receipt} />
      <KpiCard
        label="Gross Invoiced"
        value={loading ? '…' : formatPaise(totals.subtotal)}
        icon={IndianRupee}
      />
      <KpiCard
        label="Platform Net Revenue"
        value={loading ? '…' : formatPaise(totals.fee)}
        icon={TrendingUp}
        highlight
      />
      <KpiCard
        label="Associate Payout Split"
        value={loading ? '…' : formatPaise(totals.payout)}
        icon={IndianRupee}
      />
    </div>
  );
}

function GstKpis({ data, loading }: { data?: GstReportResponse; loading: boolean }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <KpiCard label="Paid Invoices" value={loading ? '…' : (data?.count ?? 0)} icon={Receipt} />
      <KpiCard
        label="Taxable Subtotal"
        value={loading ? '…' : (data?.totals ? formatPaise(data.totals.subtotalPaise) : '—')}
        icon={IndianRupee}
      />
      <KpiCard
        label="CGST + SGST (Intra-state)"
        value={
          loading
            ? '…'
            : data?.totals
            ? formatPaise(BigInt(data.totals.cgstPaise) + BigInt(data.totals.sgstPaise))
            : '—'
        }
        icon={Receipt}
      />
      <KpiCard
        label="Total Output GST"
        value={loading ? '…' : (data?.totals ? formatPaise(data.totals.totalPaise) : '—')}
        icon={TrendingUp}
        highlight
      />
    </div>
  );
}

function TdsKpis({ data, loading }: { data?: TdsReportResponse; loading: boolean }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <KpiCard
        label="Total Payouts"
        value={loading ? '…' : (data?.count ?? 0)}
        icon={Receipt}
      />
      <KpiCard
        label="Gross Payout Amount"
        value={loading ? '…' : (data?.totals ? formatPaise(data.totals.grossPaise) : '—')}
        icon={IndianRupee}
      />
      <KpiCard
        label="Total TDS Withheld"
        value={loading ? '…' : (data?.totals ? formatPaise(data.totals.tdsPaise) : '—')}
        icon={ShieldCheck}
        highlight
      />
      <KpiCard
        label="Net Disbursed"
        value={loading ? '…' : (data?.totals ? formatPaise(data.totals.netPaise) : '—')}
        icon={IndianRupee}
      />
    </div>
  );
}

function KpiCard({
  label,
  value,
  icon: Icon,
  highlight,
}: {
  label: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  highlight?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-xl border bg-white p-4 shadow-2xs',
        highlight ? 'border-brand-200 bg-brand-50/30' : 'border-gray-200',
      )}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-500">{label}</span>
        <div className={cn('rounded-lg p-1.5', highlight ? 'bg-brand-100 text-brand-700' : 'bg-gray-50 text-gray-400')}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="mt-2 text-2xl font-bold tracking-tight text-gray-900">{value}</div>
    </div>
  );
}

// ── Tables ─────────────────────────────────────────────────────────

function RevenueTable({
  data,
  loading,
  error,
}: {
  data?: RevenueReportResponse;
  loading: boolean;
  error: boolean;
}) {
  if (loading) return <TableLoadingMessage message="Calculating revenue breakdown…" />;
  if (error) return <TableErrorMessage message="Failed to load revenue report." />;
  if (!data?.items || data.items.length === 0) {
    return <TableEmptyMessage message="No revenue recorded in this date range." />;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[650px] text-left text-xs">
        <thead className="border-b border-gray-200 bg-gray-50 text-gray-600 font-semibold uppercase tracking-wider text-[11px]">
          <tr>
            <th className="px-4 py-3 whitespace-nowrap">Period Bucket</th>
            <th className="px-4 py-3 text-center whitespace-nowrap">Invoices Paid</th>
            <th className="px-4 py-3 text-right whitespace-nowrap">Gross Subtotal</th>
            <th className="px-4 py-3 text-right text-brand-800 whitespace-nowrap">Platform Fee (EcRegi)</th>
            <th className="px-4 py-3 text-right whitespace-nowrap">Associate Split</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 text-gray-700">
          {data.items.map((row) => (
            <tr key={row.bucket} className="hover:bg-gray-50/80 transition-colors">
              <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">{row.bucket}</td>
              <td className="px-4 py-3 text-center whitespace-nowrap">
                <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800">
                  {row.invoiceCount}
                </span>
              </td>
              <td className="px-4 py-3 text-right font-medium whitespace-nowrap">{formatPaise(row.subtotalPaise)}</td>
              <td className="px-4 py-3 text-right font-bold text-brand-700 whitespace-nowrap">
                {formatPaise(row.platformFeePaise)}
              </td>
              <td className="px-4 py-3 text-right font-medium text-gray-600 whitespace-nowrap">
                {formatPaise(row.associatePayoutPaise)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GstTable({
  data,
  loading,
  error,
}: {
  data?: GstReportResponse;
  loading: boolean;
  error: boolean;
}) {
  if (loading) return <TableLoadingMessage message="Generating GST register…" />;
  if (error) return <TableErrorMessage message="Failed to load GST report." />;
  if (!data?.items || data.items.length === 0) {
    return <TableEmptyMessage message="No paid invoices found in this date range." />;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[850px] text-left text-xs">
        <thead className="border-b border-gray-200 bg-gray-50 text-gray-600 font-semibold uppercase tracking-wider text-[11px]">
          <tr>
            <th className="px-4 py-3 whitespace-nowrap">Invoice #</th>
            <th className="px-4 py-3 whitespace-nowrap">Tx Code</th>
            <th className="px-4 py-3 whitespace-nowrap">Date</th>
            <th className="px-4 py-3 whitespace-nowrap">State / POS</th>
            <th className="px-4 py-3 whitespace-nowrap">Client GSTIN</th>
            <th className="px-4 py-3 whitespace-nowrap">HSN/SAC</th>
            <th className="px-4 py-3 text-right whitespace-nowrap">Taxable</th>
            <th className="px-4 py-3 text-right whitespace-nowrap">CGST</th>
            <th className="px-4 py-3 text-right whitespace-nowrap">SGST</th>
            <th className="px-4 py-3 text-right whitespace-nowrap">Total</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 text-gray-700">
          {data.items.map((row) => (
            <tr key={row.id} className="hover:bg-gray-50/80 transition-colors">
              <td className="px-4 py-3 font-mono font-semibold text-gray-900 whitespace-nowrap">{row.invoiceNumber}</td>
              <td className="px-4 py-3 font-mono text-brand-700 font-medium whitespace-nowrap">#{row.transactionCode}</td>
              <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                {row.paidAt ? new Date(row.paidAt).toLocaleDateString('en-IN') : '—'}
              </td>
              <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{row.placeOfSupplyState ?? 'N/A'}</td>
              <td className="px-4 py-3 font-mono text-[11px] text-gray-600 whitespace-nowrap">
                {row.clientGstin ? (
                  <span className="rounded bg-blue-50 px-1.5 py-0.5 text-blue-700 font-medium">
                    {row.clientGstin}
                  </span>
                ) : (
                  <span className="text-gray-400">B2C</span>
                )}
              </td>
              <td className="px-4 py-3 font-mono text-gray-500 whitespace-nowrap">{row.hsnSacCode ?? '—'}</td>
              <td className="px-4 py-3 text-right whitespace-nowrap">{formatPaise(row.subtotalPaise)}</td>
              <td className="px-4 py-3 text-right text-gray-500 whitespace-nowrap">{formatPaise(row.cgstPaise)}</td>
              <td className="px-4 py-3 text-right text-gray-500 whitespace-nowrap">{formatPaise(row.sgstPaise)}</td>
              <td className="px-4 py-3 text-right font-bold text-gray-900 whitespace-nowrap">{formatPaise(row.totalPaise)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TdsTable({
  data,
  loading,
  error,
}: {
  data?: TdsReportResponse;
  loading: boolean;
  error: boolean;
}) {
  if (loading) return <TableLoadingMessage message="Generating TDS Form 26Q register…" />;
  if (error) return <TableErrorMessage message="Failed to load TDS report." />;
  if (!data?.items || data.items.length === 0) {
    return <TableEmptyMessage message="No settled payouts with TDS found in this date range." />;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[800px] text-left text-xs">
        <thead className="border-b border-gray-200 bg-gray-50 text-gray-600 font-semibold uppercase tracking-wider text-[11px]">
          <tr>
            <th className="px-4 py-3 whitespace-nowrap">Paid Date</th>
            <th className="px-4 py-3 whitespace-nowrap">Tx Code</th>
            <th className="px-4 py-3 whitespace-nowrap">Associate</th>
            <th className="px-4 py-3 whitespace-nowrap">PAN</th>
            <th className="px-4 py-3 whitespace-nowrap">Section</th>
            <th className="px-4 py-3 text-right whitespace-nowrap">Gross</th>
            <th className="px-4 py-3 text-right text-rose-700 whitespace-nowrap">TDS Withheld</th>
            <th className="px-4 py-3 text-right font-bold whitespace-nowrap">Net Paid</th>
            <th className="px-4 py-3 whitespace-nowrap">Bank Ref</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 text-gray-700">
          {data.items.map((row) => (
            <tr key={row.id} className="hover:bg-gray-50/80 transition-colors">
              <td className="px-4 py-3 text-gray-500">
                {row.paidAt ? new Date(row.paidAt).toLocaleDateString('en-IN') : '—'}
              </td>
              <td className="px-4 py-3 font-mono text-brand-700 font-medium">#{row.transactionCode}</td>
              <td className="px-4 py-3">
                <div className="font-semibold text-gray-900">
                  {row.associate.firstName} {row.associate.lastName}
                </div>
                <div className="text-[10px] text-gray-400 font-mono">{row.associate.email}</div>
              </td>
              <td className="px-4 py-3 font-mono text-[11px]">
                {row.pan ? (
                  <span className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-700 font-semibold">
                    {row.pan}
                  </span>
                ) : (
                  <span className="text-gray-400 italic">Missing</span>
                )}
              </td>
              <td className="px-4 py-3">
                {row.sectionCode ? (
                  <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 border border-amber-200/50">
                    Sec {row.sectionCode} ({row.tdsPercentage}%)
                  </span>
                ) : (
                  <span className="text-gray-400 font-mono text-[11px]">—</span>
                )}
              </td>
              <td className="px-4 py-3 text-right font-medium">{formatPaise(row.grossPaise)}</td>
              <td className="px-4 py-3 text-right font-semibold text-rose-600">
                {formatPaise(row.tdsPaise)}
              </td>
              <td className="px-4 py-3 text-right font-bold text-gray-900">
                {formatPaise(row.netPaise)}
              </td>
              <td className="px-4 py-3 font-mono text-[11px] text-gray-500">
                {row.reference ? row.reference : <span className="text-gray-400">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Utility State Components ───────────────────────────────────────

function TableLoadingMessage({ message }: { message: string }) {
  return (
    <div className="flex h-44 flex-col items-center justify-center gap-2 text-sm text-gray-500">
      <RefreshCw className="h-5 w-5 animate-spin text-brand-600" />
      <span>{message}</span>
    </div>
  );
}

function TableErrorMessage({ message }: { message: string }) {
  return (
    <div className="flex h-44 flex-col items-center justify-center gap-1 p-6 text-center text-sm text-red-600">
      <span className="font-semibold">Unable to fetch report</span>
      <span className="text-xs text-red-500">{message}</span>
    </div>
  );
}

function TableEmptyMessage({ message }: { message: string }) {
  return (
    <div className="flex h-44 flex-col items-center justify-center gap-2 p-6 text-center text-sm text-gray-500">
      <FileSpreadsheet className="h-8 w-8 text-gray-300" />
      <span className="font-medium text-gray-700">{message}</span>
      <span className="text-xs text-gray-400">
        Try adjusting your &quot;From&quot; and &quot;To&quot; date filters above.
      </span>
    </div>
  );
}

